import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActiveSeasonsNotice from "../../../components/activeseasonsnotice/ActiveSeasonsNotice";
import styles from "./CategoryDetail.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Select,
  Button,
  Typography,
  Space,
  Skeleton,
  Alert,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  FlagOutlined,
  InboxOutlined,
  SyncOutlined,
  AlertOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  LineChartOutlined,
  LinkOutlined,
  ShopOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Legend,
  Tooltip as ChartTooltip,
  Filler,
} from "chart.js";
import {
  getAllBranchOffices,
  getAllChannels,
  getCategoryAnalysisDetailBreakdown,
  getCategoryAnalysisDetailChart,
  getCategoryAnalysisDetailCore,
} from "../../../api/Services";
import type {
  CategoryAnalysisDetailBreakdown,
  CategoryAnalysisDetailChart,
  CategoryAnalysisDetailCore,
} from "../../../types/categoryAnalysis";
import { useFetchRequestGuard } from "../../../helpers/FilterSearchHelper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Legend,
  ChartTooltip,
  Filler
);

const { Title, Text } = Typography;

const ALERT_I18N_KEYS: Record<string, string> = {
  SALES_DROP_VS_LY: "CategoryAnalysis-Alert-SalesDropVsLY",
  MARGIN_DECLINE: "CategoryAnalysis-Alert-MarginDecline",
  UNITS_DROP_VS_LY: "CategoryAnalysis-Alert-UnitsDropVsLY",
  LOW_ROTATION: "CategoryAnalysis-Alert-LowRotation",
  NO_STOCK: "CategoryAnalysis-Alert-NoStock",
};

const DRIVER_I18N_KEYS: Record<string, string> = {
  SALES_GROWTH: "CategoryAnalysis-Driver-SalesGrowth",
  UNITS_GROWTH: "CategoryAnalysis-Driver-UnitsGrowth",
  MARGIN_IMPROVE: "CategoryAnalysis-Driver-MarginImprove",
  HIGH_SHARE: "CategoryAnalysis-Driver-HighShare",
};

const SUMMARY_I18N_KEYS: Record<string, string> = {
  SALES_GROWING: "CategoryAnalysis-Summary-SalesGrowing",
  SALES_STABLE: "CategoryAnalysis-Summary-SalesStable",
  SALES_DECLINING: "CategoryAnalysis-Summary-SalesDeclining",
  MARGIN_UP: "CategoryAnalysis-Summary-MarginUp",
  MARGIN_STABLE: "CategoryAnalysis-Summary-MarginStable",
  MARGIN_DOWN: "CategoryAnalysis-Summary-MarginDown",
};

const formatCurrency = (v: number) =>
  `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;

const formatNumber = (v: number) => v.toLocaleString("es-AR");

const VariationBadge: React.FC<{ value: number; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  const isPositive = value >= 0;
  return (
    <span
      className={`${styles.variation} ${isPositive ? styles.positive : styles.negative}`}
    >
      {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {" "}{value >= 0 ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" as const, labels: { usePointStyle: true, font: { size: 12 } } },
  },
  scales: {
    y: { beginAtZero: false, grid: { color: "rgba(0,0,0,0.04)" } },
    x: { grid: { display: false } },
  },
};

const CategoryDetailKpiSkeleton: React.FC = () => (
  <div className={styles.kpiRow}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Card className={styles.kpiCard} key={i}>
        <Skeleton.Avatar active size={20} shape="square" style={{ marginBottom: 6 }} />
        <Skeleton.Input active size="small" style={{ width: 80, minWidth: 80, height: 12, marginBottom: 6 }} />
        <Skeleton.Input active style={{ width: "70%", minWidth: 90, height: 22, marginBottom: 6 }} />
        <Skeleton.Input active size="small" style={{ width: 52, minWidth: 52, height: 16 }} />
      </Card>
    ))}
  </div>
);

const CategoryDetailAlertsSkeleton: React.FC = () => (
  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
    {Array.from({ length: 3 }).map((_, i) => (
      <Col xs={24} lg={8} key={i}>
        <Card className={styles.sectionCard}>
          <Skeleton.Input active size="small" style={{ width: 140, height: 16, marginBottom: 12 }} />
          <Skeleton active paragraph={{ rows: 2, width: ["100%", "80%"] }} title={false} />
        </Card>
      </Col>
    ))}
  </Row>
);

const CategoryDetailCoreSkeleton: React.FC = () => (
  <>
    <CategoryDetailKpiSkeleton />
    <CategoryDetailAlertsSkeleton />
  </>
);

const CategoryDetailChartSkeleton: React.FC = () => (
  <>
    <Card className={styles.sectionCard}>
      <Skeleton.Input active size="small" style={{ width: 200, height: 16, marginBottom: 16 }} />
      <Skeleton.Input active style={{ width: "100%", height: 220 }} />
    </Card>
    <Card className={styles.sectionCard} style={{ marginBottom: 16 }}>
      <Skeleton active paragraph={{ rows: 2 }} />
    </Card>
  </>
);

const CategoryDetailBreakdownSkeleton: React.FC = () => (
  <>
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Col>
    </Row>
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className={styles.sectionCard}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </Col>
    </Row>
    <Card className={styles.sectionCard} style={{ marginBottom: 16 }}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 5 }} />
    </Card>
  </>
);

const CategoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const categoryId = Number(id);

  const [core, setCore] = useState<CategoryAnalysisDetailCore | null>(null);
  const [chart, setChart] = useState<CategoryAnalysisDetailChart | null>(null);
  const [breakdown, setBreakdown] = useState<CategoryAnalysisDetailBreakdown | null>(null);
  const [coreLoading, setCoreLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [chartError, setChartError] = useState(false);
  const [breakdownError, setBreakdownError] = useState(false);
  const [period, setPeriodo] = useState(30);
  const [channel, setCanal] = useState<number | undefined>();
  const [store, setTienda] = useState<number | undefined>();
  const [channelOptions, setChannelOptions] = useState<{ value: number; label: string }[]>([]);
  const [storeOptions, setStoreOptions] = useState<{ value: number; label: string }[]>([]);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();

  useEffect(() => {
    getAllChannels().then((channels: { id: number; type?: { type?: string } }[]) => {
      setChannelOptions(
        channels.map((c) => ({ value: c.id, label: c.type?.type ?? String(c.id) }))
      );
    });
    getAllBranchOffices().then((stores) => {
      setStoreOptions(
        (stores ?? []).map((b: { id: number; name: string }) => ({ value: b.id, label: b.name }))
      );
    });
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!categoryId) return;
    const requestId = beginRequest();
    setCoreLoading(true);
    setChartLoading(true);
    setBreakdownLoading(true);
    setChartError(false);
    setBreakdownError(false);

    const params = { period, channelId: channel, branchOfficeId: store };
    const coreResult = await getCategoryAnalysisDetailCore(categoryId, params);
    if (isStaleRequest(requestId)) return;
    setCore(coreResult);
    setCoreLoading(false);
    if (!coreResult) {
      setChartLoading(false);
      setBreakdownLoading(false);
      setChartError(true);
      setBreakdownError(true);
      return;
    }

    // Load chart and breakdown independently so one timeout doesn't hide the other.
    getCategoryAnalysisDetailChart(categoryId, params).then((chartResult) => {
      if (isStaleRequest(requestId)) return;
      setChart(chartResult);
      setChartError(!chartResult);
      setChartLoading(false);
    });
    getCategoryAnalysisDetailBreakdown(categoryId, params).then((breakdownResult) => {
      if (isStaleRequest(requestId)) return;
      setBreakdown(breakdownResult);
      setBreakdownError(!breakdownResult);
      setBreakdownLoading(false);
    });
  }, [categoryId, period, channel, store, beginRequest, isStaleRequest]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const filterBar = (
    <div className={styles.headerFilters}>
      <Select
        value={String(period)}
        onChange={(v) => setPeriodo(Number(v))}
        className={styles.filterSelect}
        options={[
          { value: "30", label: t("CategoryAnalysis-Last30Days") },
          { value: "60", label: t("CategoryAnalysis-Last60Days") },
          { value: "90", label: t("CategoryAnalysis-Last90Days") },
          { value: "180", label: t("CategoryAnalysis-Last180Days") },
        ]}
      />
      <Select
        placeholder={t("Common-Channel")}
        allowClear
        value={channel}
        onChange={setCanal}
        className={styles.filterSelect}
        options={channelOptions}
      />
      <Select
        placeholder={t("Common-BranchOffice")}
        allowClear
        value={store}
        onChange={setTienda}
        className={styles.filterSelect}
        options={storeOptions}
      />
    </div>
  );

  const chartData = useMemo(() => {
    const historicalLabels = (chart?.historicalSales ?? []).map((p) => p.label);
    const forecastLabels = (chart?.forecastSales ?? []).map((p) => p.label);
    const allLabels = [
      ...historicalLabels,
      ...forecastLabels.filter((l) => !historicalLabels.includes(l)),
    ];
    const historicalData = allLabels.map((l) => {
      const point = (chart?.historicalSales ?? []).find((p) => p.label === l);
      return point ? point.sales : null;
    });
    const forecastData = allLabels.map((l) => {
      const point = (chart?.forecastSales ?? []).find((p) => p.label === l);
      return point ? point.sales : null;
    });
    return {
      labels: allLabels,
      datasets: [
        {
          label: t("CategoryAnalysis-ActualSales"),
          data: historicalData,
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139, 92, 246, 0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
          spanGaps: false,
        },
        ...(forecastLabels.length > 0
          ? [
              {
                label: t("CategoryAnalysis-Forecast"),
                data: forecastData,
                borderColor: "#f97316",
                backgroundColor: "rgba(249, 115, 22, 0.06)",
                fill: true,
                tension: 0.3,
                pointRadius: 2,
                borderWidth: 2,
                borderDash: [5, 4],
                spanGaps: false,
              },
            ]
          : []),
      ],
    };
  }, [chart, t]);

  const channelCols = [
    { title: t("Common-Channel"), dataIndex: "channel", key: "channel" },
    {
      title: t("CategoryAnalysis-Sales"),
      dataIndex: "sales",
      key: "sales",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
    },
    {
      title: t("Common-Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (v: number) => formatNumber(v ?? 0),
      align: "right" as const,
    },
    {
      title: t("CategoryAnalysis-Presence"),
      dataIndex: "presence",
      key: "presence",
      render: (v: string) => <Tag color={v === "Present" ? "green" : "default"}>{v}</Tag>,
    },
    {
      title: t("CategoryAnalysis-Variation"),
      dataIndex: "variation",
      key: "variation",
      render: (v: number) => <VariationBadge value={v} />,
      align: "right" as const,
    },
  ];

  const storeCols = [
    { title: t("Common-BranchOffice"), dataIndex: "store", key: "store" },
    {
      title: t("CategoryAnalysis-Sales"),
      dataIndex: "sales",
      key: "sales",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
    },
    {
      title: t("Common-Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (v: number) => formatNumber(v ?? 0),
      align: "right" as const,
    },
    {
      title: t("CategoryAnalysis-Presence"),
      dataIndex: "presence",
      key: "presence",
      render: (v: string) => <Tag color={v === "Present" ? "green" : "default"}>{v}</Tag>,
    },
    {
      title: t("CategoryAnalysis-Variation"),
      dataIndex: "variation",
      key: "variation",
      render: (v: number) => <VariationBadge value={v} />,
      align: "right" as const,
    },
  ];

  const productCols = [
    { title: t("CategoryAnalysis-Product"), dataIndex: "product", key: "product" },
    {
      title: t("CategoryAnalysis-Sales"),
      dataIndex: "sales",
      key: "sales",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
    },
    {
      title: t("Common-Units"),
      dataIndex: "units",
      key: "units",
      render: (v: number) => formatNumber(v),
      align: "right" as const,
    },
  ];

  const promoCols = [
    { title: t("Common-Promotion"), dataIndex: "name", key: "name" },
    {
      title: t("CategoryAnalysis-Uplift"),
      dataIndex: "uplift",
      key: "uplift",
      align: "center" as const,
      render: (v: string) => <Tag color="default">{v}</Tag>,
    },
    {
      title: t("Common-ROI"),
      dataIndex: "roi",
      key: "roi",
      align: "center" as const,
      render: (v: string) => <Tag color="default">{v}</Tag>,
    },
    {
      title: t("Common-Units"),
      dataIndex: "units",
      key: "units",
      render: (v: number) => formatNumber(v),
      align: "right" as const,
    },
  ];

  const complementaryCols = [
    {
      title: t("Common-Category"),
      dataIndex: "fullName",
      key: "category",
      render: (v: string, r: any) => v || r.category,
    },
    {
      title: t("Common-Type"),
      dataIndex: "type",
      key: "type",
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: t("CategoryAnalysis-Affinity"),
      dataIndex: "affinity",
      key: "affinity",
      align: "right" as const,
      render: (v: number) => `${(v * 100).toFixed(1)}%`,
    },
  ];

  if (coreLoading && !core) {
    return (
      <div className={styles.wrapper}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/commercial-analysis/categories")}
          type="text"
          className={styles.backBtn}
        >
          {t("CategoryAnalysis-BackToCategories")}
        </Button>
        <Card className={styles.headerCard}>
          <div className={styles.headerRow}>
            <div>
              <Skeleton.Input active style={{ width: 320, height: 32, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 240, height: 16 }} />
            </div>
            <div className={styles.headerRight}>{filterBar}</div>
          </div>
        </Card>
        <CategoryDetailCoreSkeleton />
        <CategoryDetailChartSkeleton />
        <CategoryDetailBreakdownSkeleton />
      </div>
    );
  }

  if (!core) {
    return (
      <div className={styles.wrapper}>
        <Alert type="error" message={t("CategoryAnalysis-LoadError")} />
      </div>
    );
  }

  const ventaVariation =
    core.grossSalesLY > 0
      ? ((core.grossSales - core.grossSalesLY) / core.grossSalesLY) * 100
      : 0;
  const unidadesVariation =
    core.unitsLY > 0 ? ((core.units - core.unitsLY) / core.unitsLY) * 100 : 0;
  const margenVariation = (core.margin ?? 0) - (core.marginLY ?? 0);
  const rotacionVariation = (core.turnover ?? 0) - (core.turnoverLY ?? 0);

  return (
    <div className={styles.wrapper}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/commercial-analysis/categories")}
        type="text"
        className={styles.backBtn}
      >
        {t("CategoryAnalysis-BackToCategories")}
      </Button>
      <Card className={styles.headerCard}>
        <div className={styles.headerRow}>
          <div>
            <Title level={3} className={styles.pageTitle}>
              {core.fullName ?? core.name}
            </Title>
            <div className={styles.metaRow}>
              <div className={styles.metaItem}>
                <Tag color="purple">N{core.level}</Tag>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>
                  {t("CategoryAnalysis-SalesShareLabel")}
                </span>
                <span className={styles.metaValue}>
                  {(core.salesShare ?? 0).toFixed(1)}%
                </span>
              </div>
              <div className={styles.metaItem}>
                <TagOutlined className={styles.metaIcon} />
                <span className={styles.metaLabel}>
                  {t("CategoryAnalysis-ActiveProducts")}
                </span>
                <span className={styles.metaValue}>{formatNumber(core.products ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerRight}>{filterBar}</div>
        </div>
      </Card>

      <div className={styles.kpiRow}>
        {[
          {
            label: t("CategoryAnalysis-GrossSales"),
            value: formatCurrency(core.grossSales ?? 0),
            variation: ventaVariation,
            icon: <ShoppingCartOutlined />,
          },
          {
            label: t("Common-Units"),
            value: formatNumber(core.units ?? 0),
            variation: unidadesVariation,
            icon: <TagOutlined />,
          },
          {
            label: t("Common-Margin"),
            value: `${(core.margin ?? 0).toFixed(1)}%`,
            variation: margenVariation,
            icon: <FlagOutlined />,
            suffix: " pp",
          },
          {
            label: t("Common-Stock"),
            value: formatNumber(core.stock ?? 0),
            icon: <InboxOutlined />,
          },
          {
            label: t("CategoryAnalysis-Rotation"),
            value: (core.turnover ?? 0).toFixed(2),
            variation: rotacionVariation,
            icon: <SyncOutlined />,
            suffix: "",
          },
        ].map((kpi, idx) => (
          <Card className={styles.kpiCard} key={idx}>
            <div className={styles.kpiIcon}>{kpi.icon}</div>
            <Text className={styles.kpiLabel}>{kpi.label}</Text>
            <div className={styles.kpiValue}>{kpi.value}</div>
            {kpi.variation !== undefined && (
              <VariationBadge value={kpi.variation} suffix={kpi.suffix ?? "%"} />
            )}
          </Card>
        ))}
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <AlertOutlined style={{ color: "#f5222d" }} />
                <span>{t("CategoryAnalysis-ActiveAlerts")}</span>
              </Space>
            }
          >
            {core.alerts && core.alerts.length > 0 ? (
              <div className={styles.bulletList}>
                {core.alerts.map((key) => (
                  <div className={styles.bulletItem} key={key}>
                    <AlertOutlined className={styles.bulletIconRed} />
                    <span>{t(ALERT_I18N_KEYS[key] ?? key)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">{t("CategoryAnalysis-NoActiveAlerts")}</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ThunderboltOutlined style={{ color: "#1890ff" }} />
                <span>{t("CategoryAnalysis-DetectedDrivers")}</span>
              </Space>
            }
          >
            {core.drivers && core.drivers.length > 0 ? (
              <div className={styles.bulletList}>
                {core.drivers.map((key) => (
                  <div className={styles.bulletItem} key={key}>
                    <ThunderboltOutlined className={styles.bulletIconBlue} />
                    <span>{t(DRIVER_I18N_KEYS[key] ?? key)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">{t("CategoryAnalysis-NoDrivers")}</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <BulbOutlined style={{ color: "#faad14" }} />
                <span>{t("CategoryAnalysis-PerformanceSummary")}</span>
              </Space>
            }
          >
            {core.performanceSummary && core.performanceSummary.length > 0 ? (
              <div className={styles.bulletList}>
                {core.performanceSummary.map((key) => (
                  <div className={styles.bulletItem} key={key}>
                    <BulbOutlined className={styles.bulletIconYellow} />
                    <span>{t(SUMMARY_I18N_KEYS[key] ?? key)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">{t("CategoryAnalysis-Calculating")}</Text>
            )}
          </Card>
        </Col>
      </Row>

      {chartLoading ? (
        <CategoryDetailChartSkeleton />
      ) : chartError ? (
        <Alert
          type="error"
          message={t("CategoryAnalysis-LoadError")}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <LineChartOutlined />
                <span>{t("CategoryAnalysis-SalesEvolution")}</span>
              </Space>
            }
          >
            <div className={styles.chartContainer}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </Card>
        </>
      )}

      {breakdownLoading ? (
        <CategoryDetailBreakdownSkeleton />
      ) : breakdownError ? (
        <Alert type="error" message={t("CategoryAnalysis-LoadError")} />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={12}>
              <Card
                className={styles.sectionCard}
                title={
                  <Space>
                    <ShopOutlined />
                    <span>{t("CategoryAnalysis-PerformanceByChannel")}</span>
                  </Space>
                }
              >
                <Table
                  columns={channelCols}
                  dataSource={(breakdown?.channelPerformance ?? []).map((r, i) => ({
                    ...r,
                    key: r.channelId ?? i,
                  }))}
                  pagination={false}
                  size="small"
                  className={styles.dataTable}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                className={styles.sectionCard}
                title={
                  <Space>
                    <ShopOutlined />
                    <span>{t("CategoryAnalysis-PerformanceByStore")}</span>
                  </Space>
                }
              >
                <Table
                  columns={storeCols}
                  dataSource={(breakdown?.storePerformance ?? []).map((r, i) => ({
                    ...r,
                    key: r.branchOfficeId ?? i,
                  }))}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  size="small"
                  scroll={{ y: 400 }}
                  className={`${styles.dataTable} ${styles.scrollableTable}`}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={12}>
              <Card
                className={styles.sectionCard}
                title={
                  <Space>
                    <TrophyOutlined style={{ color: "#52c41a" }} />
                    <span>{t("CategoryAnalysis-TopProducts")}</span>
                  </Space>
                }
              >
                <Table
                  columns={productCols}
                  dataSource={(breakdown?.topProducts ?? []).map((r, i) => ({
                    ...r,
                    key: r.articleId ?? i,
                  }))}
                  pagination={{ pageSize: 5, size: "small", hideOnSinglePage: true }}
                  size="small"
                  className={styles.dataTable}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                className={styles.sectionCard}
                title={
                  <Space>
                    <ArrowDownOutlined style={{ color: "#f5222d" }} />
                    <span>{t("CategoryAnalysis-BottomProducts")}</span>
                  </Space>
                }
              >
                <ActiveSeasonsNotice />
                <Table
                  columns={productCols}
                  dataSource={(breakdown?.bottomProducts ?? []).map((r, i) => ({
                    ...r,
                    key: r.articleId ?? i,
                  }))}
                  pagination={{ pageSize: 5, size: "small", hideOnSinglePage: true }}
                  size="small"
                  className={styles.dataTable}
                />
              </Card>
            </Col>
          </Row>

          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <ShopOutlined />
                <span>{t("CategoryAnalysis-ParticipatingPromotions")}</span>
              </Space>
            }
          >
            <Table
              columns={promoCols}
              dataSource={(breakdown?.promotions ?? []).map((r, i) => ({
                ...r,
                key: r.promotionId ?? i,
              }))}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              size="small"
              scroll={{ y: 400 }}
              className={`${styles.dataTable} ${styles.scrollableTable}`}
            />
          </Card>

          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <LinkOutlined />
                <span>{t("CategoryAnalysis-ComplementaryCategories")}</span>
              </Space>
            }
          >
            <Table
              columns={complementaryCols}
              dataSource={(breakdown?.complementaryCategories ?? []).map((r, i) => ({
                ...r,
                key: r.categoryId ?? i,
              }))}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              size="small"
              scroll={{ y: 400 }}
              className={`${styles.dataTable} ${styles.scrollableTable}`}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default CategoryDetail;
